import clsx from 'clsx';
import { useParams } from 'react-router-dom';
import { UserRoundPlus } from '@/components/ui/icons';
import { useDb, useStore } from '@/lib/store';
import { initials } from '@/lib/hierarchy';
import { Tooltip } from './Tooltip';

export function Avatar({
  userId,
  size = 24,
  showPresence = false,
  tooltip = false,
  className,
  ring,
}: {
  userId: string;
  size?: number;
  showPresence?: boolean;
  tooltip?: boolean;
  className?: string;
  ring?: boolean;
}) {
  const user = useDb((db) => db.users[userId]);
  const online = useStore((s) => showPresence && s.presence.some((p) => p.userId === userId));
  // guests wear a square marker instead of a dot
  const ws = useParams().ws;
  const guest = useDb((db) => showPresence && !!ws && db.workspaces[ws]?.roles?.[userId] === 'guest');
  const name = user?.name ?? '?';
  const el = (
    <span
      className={clsx('avatar', className, { ring })}
      style={{ width: size, height: size, fontSize: Math.max(9, Math.round(size * 0.4)), background: user?.avatarUrl ? undefined : (user?.color ?? '#656f7d') }}
    >
      {user?.avatarUrl ? <img src={user.avatarUrl} alt="" /> : initials(name)}
      {showPresence && <span className={clsx('avatar-presence', { online, guest })} />}
    </span>
  );
  return tooltip ? <Tooltip label={name}>{el}</Tooltip> : el;
}

export function AvatarStack({ userIds, size = 24, max = 3, empty }: { userIds: string[]; size?: number; max?: number; empty?: React.ReactNode }) {
  if (userIds.length === 0)
    return (
      <>
        {empty ?? (
          <span className="avatar-empty" style={{ width: size, height: size }}>
            <UserRoundPlus size={Math.round(size * 0.62)} />
          </span>
        )}
      </>
    );
  const shown = userIds.slice(0, max);
  const extra = userIds.length - shown.length;
  return (
    <span className="avatar-stack">
      {shown.map((id) => (
        <Avatar key={id} userId={id} size={size} tooltip ring />
      ))}
      {extra > 0 && (
        <span className="avatar avatar-more ring" style={{ width: size, height: size, fontSize: Math.max(9, Math.round(size * 0.38)) }}>
          +{extra}
        </span>
      )}
    </span>
  );
}
